import { motion } from 'framer-motion';
import { ArrowUpRight, ExternalLink } from 'lucide-react';

export const Portfolio = () => {
  const projects = [
    {
      title: 'Muscle Empire Gym',
      category: 'Fitness Website',
      image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80',
      description: 'Modern fitness website with class schedules, membership plans, and trainer profiles.',
      stats: { conversion: '+75%', traffic: '4.2K/mo' },
      link: 'https://muscleempire.netlify.app/',
    },
    {
      title: 'Void Zone Gaming',
      category: 'Gaming Café',
      image: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=800&q=80',
      description: 'High-performance gaming café website with booking system and tournament management.',
      stats: { conversion: '+90%', traffic: '6K/mo' },
      link: 'https://voidzonesite.netlify.app/',
    },
    {
      title: 'The Wellness Co',
      category: 'Health & Wellness',
      image: 'https://images.unsplash.com/photo-1697082977798-72ff9de23c88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxoZWFsdGglMjBjbGluaWMlMjBtb2Rlcm4lMjBpbnRlcmlvciUyMGRhcmt8ZW58MHx8fHwxNzc1MDc5MzA0fDA&ixlib=rb-4.1.0&q=85',
      description: 'Professional wellness platform with therapy services, booking system, and client portal.',
      stats: { conversion: '+100%', traffic: '8K/mo' },
      link: 'https://www.thewellnessco.in/therapies',
    },
  ];

  return (
    <section id="portfolio" className="py-24 md:py-32 relative" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#007AFF] mb-4 block" data-testid="portfolio-label">
            Our Work
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="portfolio-title">
            Featured{' '}
            <span className="bg-gradient-to-r from-[#007AFF] to-[#00F0FF] bg-clip-text text-transparent">
              Projects
            </span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto leading-relaxed" data-testid="portfolio-description">
            Real results for real businesses. See how we've helped our clients transform their online presence.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <motion.a
              key={index}
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="glass-card rounded-2xl overflow-hidden hover:border-[#007AFF] transition-all cursor-hover group block"
              data-testid={`project-card-${index}`}
            >
              <div className="relative overflow-hidden aspect-[4/3]">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-[#007AFF] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all transform translate-y-4 group-hover:translate-y-0">
                  <ExternalLink className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="p-6">
                <span className="text-xs uppercase tracking-wider text-[#007AFF] font-bold" data-testid={`project-category-${index}`}>
                  {project.category}
                </span>
                <h3 className="text-xl font-semibold mt-2 mb-3" data-testid={`project-title-${index}`}>
                  {project.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-4" data-testid={`project-description-${index}`}>
                  {project.description}
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-white/5">
                  <div>
                    <div className="text-lg font-bold text-[#00F0FF]">{project.stats.conversion}</div>
                    <div className="text-xs text-zinc-500">Conversion</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-[#00F0FF]">{project.stats.traffic}</div>
                    <div className="text-xs text-zinc-500">Traffic</div>
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};